<script lang="ts" setup>
import type { EmblaOptionsType } from 'embla-carousel';

import { useCarousel } from '~kui/composables/useCarousel';
import { useCarouselPlugins } from '~kui/composables/useCarouselPlugins';

interface Props {
  // AutoScroll options
  speed?: number;
  direction?: 'forward' | 'backward';
  stopOnMouseEnter?: boolean;
  startDelay?: number;

  // Core carousel options
  loop?: boolean;
  align?: 'start' | 'center' | 'end';
  duration?: number;

  // Custom classes
  className?: string;
  containerClassName?: string;
  slideClassName?: string;
  viewportClassName?: string;
}

const props = withDefaults(defineProps<Props>(), {
  speed: 1,
  direction: 'forward',
  stopOnMouseEnter: true,
  startDelay: 1000,

  loop: true,
  align: 'start',
  duration: 25,

  className: '',
  containerClassName: undefined,
  slideClassName: undefined,
  viewportClassName: undefined,
});

// Build embla options
const emblaOptions = computed<EmblaOptionsType>(() => ({
  loop: props.loop,
  align: props.align,
  duration: props.duration,
  dragFree: false,
  breakpoints: {
    '(min-width: 768px)': {
      slidesToScroll: 2,
      align: 'start',
    },
    '(min-width: 1024px)': {
      slidesToScroll: 3,
      align: 'start',
    },
  },
}));

// Build plugins
const emblaPlugins = computed(() =>
  useCarouselPlugins({
    autoScroll: {
      speed: props.speed,
      direction: props.direction,
      stopOnMouseEnter: props.stopOnMouseEnter,
      startDelay: props.startDelay,
      playOnInit: true,
      stopOnInteraction: true,
    },
  }),
);

// Initialize carousel
const { emblaRef, slides } = useCarousel({
  options: emblaOptions,
  plugins: emblaPlugins,
});

// Computed classes
const wrapperClasses = computed(() => twMerge('relative', props.className));

const viewportClasses = computed(() =>
  twMerge('mx-auto w-full overflow-hidden', props.viewportClassName),
);

const containerClasses = computed(() =>
  twMerge(
    'flex touch-pan-y touch-pinch-zoom items-center',
    props.containerClassName,
  ),
);

const slideClasses = computed(() =>
  twMerge(
    'min-w-0 flex-[0_0_50%] md:flex-[0_0_33.333%] lg:flex-[0_0_25%]',
    props.slideClassName,
  ),
);
</script>

<template>
  <div
    :class="wrapperClasses"
    role="region"
    aria-label="Auto-scrolling carousel"
  >
    <div ref="emblaRef" :class="viewportClasses">
      <div :class="containerClasses" role="list">
        <div
          v-for="(slide, index) in slides"
          :key="`embla-slide-auto-scroll-${index}`"
          :class="slideClasses"
          role="listitem"
        >
          <component :is="slide" />
        </div>
      </div>
    </div>
  </div>
</template>
